/*
 * Copyright (c) 2023. All rights are reserved to Newport Collection
 */

export function getOrganization(name, url) {
	return { '@type': 'Organization', name, url };
}

export function getBreadcrumbsItemListElement(bcObject, baseUrl, isCms = false) {
	let position = isCms ? 0 : 1;
	const ItemListElement = [];
	if (!isCms) {
		ItemListElement.push({
			'@type': 'ListItem',
			position: position,
			name: 'Shop',
			item: `${baseUrl}shop`
		});
	}
	for (let key in bcObject) {
		if (bcObject.hasOwnProperty(key) && !bcObject[key].path?.includes('/navigation/')) {
			position++;
			ItemListElement.push({
				'@type': 'ListItem',
				position: position,
				name: bcObject[key].name,
				item: `${baseUrl}${
					bcObject[key].path.indexOf('/') === 0 ? bcObject[key].path.replace('/', '') : bcObject[key].path
				}`
			});
		}
	}
	return ItemListElement;
}

export function getKeywords(entity) {
	let keywords = 'Newport, ';
	if (!!entity.breadcrumbs) {
		keywords += [...entity.breadcrumb?.filter((bc, i) => i !== 0 && i !== 1)].join(', ');
	} else if (entity.translated?.name) {
		keywords += `, ${entity.translated?.name}`;
	}

	return keywords;
}

export function getCollectionSchema(data) {
	const { collection, breadcrumb, orgName, baseUrl } = data;
	let CollectionSchema = {
		'@context': 'https://schema.org',
		'@type': 'CollectionPage',
		name: collection.translated.name,
		description: collection.translated?.metaDescription || collection.translated?.description,
		url: collection.seoUrls && collection.seoUrls.length > 0 ? `${baseUrl}/${collection.seoUrls[0]?.seoPathInfo}` : undefined,
		breadcrumb: {
			'@type': 'BreadcrumbList',
			itemListElement: getBreadcrumbsItemListElement(breadcrumb, baseUrl)
		},
		about: {
			'@type': 'Thing',
			name: `Home Interior - ${collection.translated.name}`
		},
		keywords: getKeywords(collection),
		publisher: getOrganization(orgName, baseUrl),
		audience: {
			'@type': 'Audience',
			audienceType: 'https://schema.org/GeneralAudience'
		}
	};

	return CollectionSchema;
}

export function getWebSiteSchema(data) {
	const { searchUrl, cmsPage, name, metaTitle, description, keywords, url, languageCode } = data;
	const today = new Date().toLocaleDateString(languageCode);
	const copyrightYear = new Date().getFullYear(languageCode);

	let WebsiteSchema = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: metaTitle || name,
		url,
		description,
		keywords,
		datePublished: today,
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': url
		},
		publisher: getOrganization(name, url),
		copyrightHolder: getOrganization(name, url),
		copyrightYear,
		accessibilityAPI: ['ARIA', 'WebSchemas'],
		accessibilityControl: ['fullMouseControl', 'fullKeyboardControl'],
		accessibilityFeature: ['alternativeText', 'longDescription'],
		isAccessibleForFree: true,
		isFamilyFriendly: true,
		audience: {
			'@type': 'Audience',
			audienceType: 'https://schema.org/GeneralAudience'
		},
		potentialAction: [
			{
				'@type': 'SearchAction',
				target: `${url}${searchUrl}?q={term}`,
				'query-input': 'required name=term'
			},
			{
				'@type': 'BuyAction',
				target: `${url}/shop`
			},
			{
				'@type': 'SubscribeAction',
				target: url
			}
		],
		about: {
			'@type': 'Thing',
			name: 'Home Interiors'
		},
		hasPart: getParts(cmsPage, url, description),
		isPartOf: {
			'@type': 'WebSite',
			name,
			url
		},
		headline: description,
		text: description,
		inLanguage: languageCode,
		interactionStatistic: {
			'@type': 'InteractionCounter',
			interactionType: 'https://schema.org/Comment',
			userInteractionCount: 160000
		}
	};

	return WebsiteSchema;
}

export function getRelatedLinks(tree, baseUrl) {
	if(!tree) {
		return;
	}

	return tree?.map(item => {
		const url = baseUrl + item.url.replace(/^\/|\/$/g, '');

		return url;
	});
}

export function getWebPageSchema(data) {
	const { appData, category, breadcrumb, name, metaTitle, description, keywords, baseUrl, languageCode } = data;
	const today = new Date().toLocaleDateString(languageCode);
	const url = baseUrl.replace(/\/$/, "") + (breadcrumb[category.id] ? breadcrumb[category.id].path : '');

	let WebPageSchema = {
		'@context': 'https://schema.org',
		'@type': 'WebPage',
		name: metaTitle,
		url,
		breadcrumb: getBreadcrumbsItemListElement(breadcrumb, baseUrl, true),
		description,
		keywords: `${keywords}, ${metaTitle}`,
		mainContentOfPage: {
			'@type': 'WebPageElement',
			cssSelector: '.cms-page'
		},
		relatedLink: getRelatedLinks(appData?.footer[2]?.children, baseUrl),
		inLanguage: languageCode,
		lastReviewed: today,
		datePublished: today,
		publisher: getOrganization(name, baseUrl),
		audience: {
			'@type': 'Audience',
			audienceType: 'https://schema.org/GeneralAudience'
		},
		accessMode: 'textual',
		accessibilityAPI: ['ARIA', 'WebSchemas'],
		accessibilityControl: ['fullMouseControl', 'fullKeyboardControl'],
		accessibilityFeature: ['alternativeText', 'longDescription'],
		isAccessibleForFree: true,
		about: {
			'@type': 'Thing',
			name: 'Home Interiors'
		}
	};

	if (!!breadcrumb[category.parentId] && breadcrumb[category.parentId].path?.indexOf('/navigation/') < 0) {
		WebPageSchema.isPartOf = {
			'@type': 'WebPage',
			name: breadcrumb[category.parentId].name,
			url: `${baseUrl}${
				breadcrumb[category.parentId].path.indexOf('/') === 0
					? breadcrumb[category.parentId].path.replace('/', '')
					: breadcrumb[category.parentId].path
			}`
		};
	}
	if (category.media?.url) {
		WebPageSchema.primaryImageOfPage = {
			'@type': 'ImageObject',
			url: category.media?.url,
			width: category.media?.metaData.width,
			height: category.media?.metaData.height
		};
	}
	return WebPageSchema;
}

function getParts(cmsPage, url, description) {
	const parts = [];
	cmsPage.sections.map(section => {
		section.blocks.map(block => {
			block.slots.map(slot => {
				if (!!slot.config?.url?.value && !!slot.config?.title?.value) {
					const hasSlash =
						slot.config?.url?.value.indexOf('https') < 0 && slot.config?.url?.value?.indexOf('/') === 0;
					const partUrl = hasSlash ? slot.config?.url?.value.substring(1) : slot.config?.url?.value;
					parts.push({
						'@type': 'WebPage',
						url:
							slot.config?.url?.value.indexOf('https') < 0 ? `${url}${partUrl}` : slot.config?.url?.value,
						name: slot.config?.title?.value,
						description:
							!!slot.config?.description?.value && !hasHtml(slot.config?.description?.value)
								? slot.config?.description?.value
								: description
					});
				}
			});
		});
	});
	return parts;
}

function hasHtml(text) {
	return (
		text.indexOf('<div') !== -1 ||
		text.indexOf('<p') !== -1 ||
		text.indexOf('<span') !== -1 ||
		text.indexOf('<a') !== -1 ||
		text.indexOf('<button') !== -1
	);
}
